import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/contact/ContactForm';
import ContactInfo from '../components/contact/ContactInfo';
import { 
  Phone, Clock, MapPin, Award, Wrench, Droplet, CheckCircle, XCircle, 
  DollarSign, Shield, AlertTriangle, Flame, Home, Users, Gauge, 
  ShieldCheck, UserCheck, Thermometer, Star, ArrowRight, Calendar,
  Battery, Zap, Settings, Wrench as ToolIcon, Building, Eye
} from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';

const WaterHeaterPricing = () => {
  // BirdEye widget loading
  useEffect(() => {
    const loadBirdEyeWidget = () => {
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    loadBirdEyeWidget();

    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  // SEO Meta Tags
  useEffect(() => {
    document.title = "Water Heater Prices | See Exact Cost | Colorado First";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Colorado\'s ONLY plumber with Actual Upfront Prices. 40 Gal Special: $2,000. 50 Gal: $2,100. Power Vent: $4,000. See all our flat-rate, pricing.');
    }
  }, []);

  // Product Schemas with Embedded FAQs
  
  // Rheem 40 Gallon Product Schema
  const rheem40ProductSchema = {
    "@type": "Product",
    "name": "Rheem 40 Gallon Atmospheric Water Heater - Fully Installed",
    "image": [
      "https://colorado-plumbing.com/colorado-plumbing-uploads/locations/aurora/rheem-40-gallon-standard-replaced-aurora.JPEG"
    ],
    "description": "Flat-rate $2,000 installation of a Rheem 40-gallon atmospheric gas water heater in Denver metro. Includes all labor, code compliance, expansion tank",
    "brand": {
      "@type": "Brand",
      "name": "Rheem"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://colorado-plumbing.com/water-heater-pricing#rheem-40",
      "price": "2000",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What's included in the $2,000 Rheem special?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Professional removal of your old heater, full installation of a new Rheem 40-gallon unit, code compliance, connections, new expansion tank"
          }
        },
        {
          "@type": "Question",
          "name": "Is 40 gallons enough for my home?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A 40-gallon water heater suits 1-3 people with typical use. For bigger households, consider a 50-gallon unit."
          }
        },
        {
          "@type": "Question",
          "name": "What type of venting does this require?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Requires existing vertical metal vent (atmospheric venting)."
          }
        }
      ]
    }
  };

  // Rheem 50 Gallon Product Schema
  const rheem50ProductSchema = {
    "@type": "Product",
    "name": "Rheem 50 Gallon Atmospheric Water Heater - Fully Installed",
    "image": [
      "https://colorado-plumbing.com/page-specific-content/water-heater-pricing/affordable-rheem-40-gallon-atmospheric.JPEG"
    ],
    "description": "Flat-rate $2,100 installation of a Rheem 50-gallon atmospheric gas water heater. All labor, code upgrades Great for 3–5 person households.",
    "brand": {
      "@type": "Brand",
      "name": "Rheem"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://colorado-plumbing.com/water-heater-pricing#rheem-50",
      "price": "2100",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Why choose the 50 gallon over 40 gallon?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The 50-gallon model provides 25% more hot water for just $100 more. Best for families with multiple fixtures running at once."
          }
        },
        {
          "@type": "Question",
          "name": "How long will installation take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most jobs are completed in 2–3 hours, including old unit removal, new install, and testing."
          }
        },
        {
          "@type": "Question",
          "name": "",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Extended options available."
          }
        }
      ]
    }
  };

  // Bradford White 40 Gallon Product Schema
  const bradford40ProductSchema = {
    "@type": "Product",
    "name": "Bradford White 40 Gallon Atmospheric Water Heater - Pro Installed",
    "image": [
      "https://colorado-plumbing.com/page-specific-content/water-heater-pricing/affordable-bradford-white-40-gallon.JPEG"
    ],
    "description": "Professional-grade Bradford White 40-gallon gas water heater installed for $2,300. Includes Vitraglas tank lining, Hydrojet sediment system, and code-compliant installation.",
    "brand": {
      "@type": "Brand",
      "name": "Bradford White"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://colorado-plumbing.com/water-heater-pricing#bradford-40",
      "price": "2300",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Why is Bradford White more expensive?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Professional-grade construction, Vitraglas lining, Hydrojet system, and American-made quality account for the price difference."
          }
        },
        {
          "@type": "Question",
          "name": "What is the Hydrojet® system?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Hydrojet stirs incoming cold water to reduce sediment and extend tank lifespan."
          }
        },
        {
          "@type": "Question",
          "name": "Are Bradford White units sold in stores?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No, only through licensed professionals—proper install and support guaranteed."
          }
        }
      ]
    }
  };

  // Bradford White 50 Gallon Product Schema
  const bradford50ProductSchema = {
    "@type": "Product",
    "name": "Bradford White 50 Gallon Atmospheric Water Heater - Pro Installed",
    "image": [
      "https://colorado-plumbing.com/colorado-plumbing-uploads/locations/arvada/bradford-white-50-gallon-replace-arvada.JPEG"
    ],
    "description": "Professional installation of a Bradford White 50-gallon atmospheric gas water heater ($2, Defender Safety System, ICON diagnostics included.",
    "brand": {
      "@type": "Brand",
      "name": "Bradford White"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://colorado-plumbing.com/water-heater-pricing#bradford-50",
      "price": "2400",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is the Defender Safety System®?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Bradford White's Defender Safety System prevents flammable vapor ignition for safety."
          }
        },
        {
          "@type": "Question",
          "name": "How does ICON System™ help?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "ICON System provides diagnostics to monitor performance and error codes, making troubleshooting simpler."
          }
        },
        {
          "@type": "Question",
          "name": "Is the extra capacity worth it?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "50 gallons gives 25% more hot water than 40, ideal for families with higher hot water needs. Highly recommended."
          }
        }
      ]
    }
  };

  // Bradford White 75 Gallon Product Schema
  const bradford75ProductSchema = {
    "@type": "Product",
    "name": "Bradford White 75 Gallon Atmospheric Water Heater - Pro Installed",
    "image": [
      "https://colorado-plumbing.com/colorado-plumbing-uploads/locations/littleton/bradford-white-installed-per-code-littleton.JPEG"
    ],
    "description": "Large capacity Bradford White 75 gallon water heater installation. Price: $4,500. Perfect for high-demand households requiring maximum hot water capacity.",
    "brand": {
      "@type": "Brand",
      "name": "Bradford White"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://colorado-plumbing.com/water-heater-pricing#bradford-75",
      "price": "4500",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Who needs a 75 gallon water heater?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Perfect for large families (5+ people), homes with multiple bathrooms, or those with high hot water demand like large soaking tubs."
          }
        },
        {
          "@type": "Question",
          "name": "Why is the 75 gallon $4,500?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Larger units require special ordering, heavier duty installation equipment, and additional labor. The tank itself costs significantly more."
          }
        },
        {
          "@type": "Question",
          "name": "What about recovery time?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The 75-gallon unit has excellent recovery rates, providing continuous hot water even with heavy usage."
          }
        }
      ]
    }
  };

  // Dual 50 Gallon System Product Schema
  const dual50ProductSchema = {
    "@type": "Product",
    "name": "Bradford White Dual 50 Gallon Water Heater System - Fully Installed",
    "image": [
      "https://colorado-plumbing.com/page-specific-content/water-heater-pricing/affordable-dual-50-gallon-bradford.JPEG"
    ],
    "description": "Two Bradford White 48-50 gallon atmospheric water heaters providing 100 gallon total capacity. Price: $4,500. Ultimate capacity solution with redundancy.",
    "brand": {
      "@type": "Brand",
      "name": "Bradford White"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://colorado-plumbing.com/water-heater-pricing#dual-50",
      "price": "4500",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Why choose dual tanks over one large unit?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Dual tanks provide redundancy (if one fails, you still have hot water), better recovery rates, and can be more space-efficient."
          }
        },
        {
          "@type": "Question",
          "name": "How does the $4,500 price compare to alternatives?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Similar to a single 75-gallon unit but with better recovery and redundancy. Much more affordable than tankless for this capacity."
          }
        },
        {
          "@type": "Question",
          "name": "What's included in the installation?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Both water heaters, all necessary plumbing connections, manifold system, code compliance, removal of old units"
          }
        }
      ]
    }
  };

  // Rheem 40 Gallon Powervent Product Schema
  const rheemPowerventProductSchema = {
    "@type": "Product",
    "name": "Rheem 40 Gallon Powervent Water Heater - Fully Installed",
    "image": [
      "https://colorado-plumbing.com/colorado-plumbing-uploads/locations/centennial/powervented-water-heater-replacement-centennial.JPEG"
    ],
    "description": "Rheem Performance 40 gallon tall powervent water heater. Price: $4,000. Energy efficient with flexible venting options for homes without traditional venting.",
    "brand": {
      "@type": "Brand",
      "name": "Rheem"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://colorado-plumbing.com/water-heater-pricing#rheem-powervent",
      "price": "4000",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Why choose power vent over atmospheric?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Power vent allows horizontal venting through walls, better efficiency, and installation flexibility when vertical venting isn't possible."
          }
        },
        {
          "@type": "Question",
          "name": "What rebates are available?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Many utility companies offer rebates for energy-efficient power vent models. We'll help you identify available incentives."
          }
        },
        {
          "@type": "Question",
          "name": "Do power vents need electricity?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, power vent water heaters require a 120V electrical outlet for the blower motor that exhausts combustion gases."
          }
        }
      ]
    }
  };

  // Bradford White 40 Gallon Powervent Product Schema
  const bradford40PowerventProductSchema = {
    "@type": "Product",
    "name": "Bradford White 40 Gallon Powervent Water Heater - Pro Installed",
    "image": [
      "https://colorado-plumbing.com/colorado-plumbing-uploads/locations/loveland/powervent-water-heater-replacement-loveland.jpeg"
    ],
    "description": "Bradford White 40 gallon powervent water heater installation. Price: $4,500. Professional grade with active venting and superior reliability.",
    "brand": {
      "@type": "Brand",
      "name": "Bradford White"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://colorado-plumbing.com/water-heater-pricing#bradford-powervent-40",
      "price": "4500",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What makes Bradford White powervent superior?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Bradford White power vents feature Hydrojet technology, Vitraglas lining, and professional-grade construction for longer life."
          }
        },
        {
          "@type": "Question",
          "name": "Is Bradford White worth the extra cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, the superior build quality, and professional-only distribution ensure better long-term value."
          }
        },
        {
          "@type": "Question",
          "name": "What venting options are available?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Power vent models can vent horizontally up to 120 feet with proper pipe sizing, offering maximum installation flexibility."
          }
        }
      ]
    }
  };

  // Bradford White 48-50 Gallon Powervent Product Schema
  const bradford50PowerventProductSchema = {
    "@type": "Product",
    "name": "Bradford White 48-50 Gallon Powervent Water Heater - Pro Installed",
    "image": [
      "https://colorado-plumbing.com/page-specific-content/water-heater-pricing/true-price-powervent-bradford-white-50.JPEG"
    ],
    "description": "Bradford White 48-50 gallon powervent water heater. Price: $4,900. Higher capacity with efficient operation and professional installation.",
    "brand": {
      "@type": "Brand",
      "name": "Bradford White"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://colorado-plumbing.com/water-heater-pricing#bradford-powervent-50",
      "price": "4900",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Why upgrade to 48-50 gallon capacity?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The larger capacity provides 25% more hot water while maintaining the efficiency benefits of power venting technology."
          }
        },
        {
          "@type": "Question",
          "name": "What's included in the $4,900 price?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Complete installation including water heater, venting materials, electrical connection, code compliance, old unit removal"
          }
        },
        {
          "@type": "Question",
          "name": "How does powervent compare to direct vent?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Power vent uses indoor air for combustion with powered exhaust. Direct vent is sealed combustion using outdoor air, ideal for tight spaces."
          }
        }
      ]
    }
  };

  // Bradford White 48 Gallon Direct Powervent Product Schema
  const bradfordDirectVentProductSchema = {
    "@type": "Product",
    "name": "Bradford White 48 Gallon Direct Powervent Water Heater - Premium Installation",
    "image": [
      "https://colorado-plumbing.com/colorado-plumbing-uploads/locations/arvada/power-direct-vent-replaced.JPEG"
    ],
    "description": "Bradford White 48 gallon direct powervent water heater. Price: $5,500. Premium sealed combustion system for maximum efficiency and safety.",
    "brand": {
      "@type": "Brand",
      "name": "Bradford White"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://colorado-plumbing.com/water-heater-pricing#bradford-direct-vent",
      "price": "5500",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360"
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What makes direct powervent worth the premium?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sealed combustion provides maximum efficiency, safety in tight spaces, and independence from indoor air quality."
          }
        },
        {
          "@type": "Question",
          "name": "What's the difference from regular powervent?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Direct vent uses two pipes - one for combustion air intake, one for exhaust. Regular power vent only exhausts, using indoor air."
          }
        },
        {
          "@type": "Question",
          "name": "What venting is required?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Requires concentric or twin pipe venting system. Can vent horizontally through wall or vertically through roof."
          }
        }
      ]
    }
  };

  // Complete Page Schema with Product + FAQ Structure
  const completePageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      // WebPage Schema
      {
        "@type": "WebPage",
        "@id": "https://colorado-plumbing.com/water-heater-pricing#webpage",
        "url": "https://colorado-plumbing.com/water-heater-pricing",
        "name": "Denver Water Heater Cost: $2k Rheem, $2.3k Bradford White | Colorado First Plumbing",
        "description": "See exact flat-rate prices for water heater installation in Denver. Rheem 40-Gal Special: $2,000. Bradford White 40-Gal: $2,300. 360 Google Reviews. No hidden fees.",
        "inLanguage": "en-US",
        "isPartOf": {
          "@id": "https://colorado-plumbing.com/#website"
        },
        "breadcrumb": {
          "@id": "https://colorado-plumbing.com/water-heater-pricing#breadcrumb"
        }
      },
      // BreadcrumbList Schema
      {
        "@type": "BreadcrumbList",
        "@id": "https://colorado-plumbing.com/water-heater-pricing#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://colorado-plumbing.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Pricing",
            "item": "https://colorado-plumbing.com/pricing"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Water Heater Pricing",
            "item": "https://colorado-plumbing.com/water-heater-pricing"
          }
        ]
      },
      // Organization/LocalBusiness Schema
      {
        "@type": "Plumber",
        "@id": "https://colorado-plumbing.com/",
        "name": "Colorado First Plumbing and Sewer LLC",
        "alternateName": "Colorado First Plumbing",
        "url": "https://colorado-plumbing.com",
        "logo": "https://colorado-plumbing.com/logo.png",
        "image": "https://colorado-plumbing.com/colorado-plumbing-uploads/master-plumber-codee-jessen.jpg",
        "email": "codee@colorado-plumbing.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "5108 Sydney Ave",
          "addressLocality": "Littleton",
          "addressRegion": "CO",
          "postalCode": "80130",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 39.53934,
          "longitude": -104.92936
        },
        "serviceArea": [
          {
            "@type": "City",
            "name": "Denver",
            "@id": "https://en.wikipedia.org/wiki/Denver"
          },
          {
            "@type": "City",
            "name": "Littleton"
          },
          {
            "@type": "City",
            "name": "Lakewood"
          },
          {
            "@type": "City",
            "name": "Highlands Ranch"
          },
          {
            "@type": "City",
            "name": "Castle Rock"
          },
          {
            "@type": "City",
            "name": "Arvada"
          },
          {
            "@type": "City",
            "name": "Thornton"
          },
          {
            "@type": "City",
            "name": "Centennial"
          },
          {
            "@type": "City",
            "name": "Longmont"
          }
        ],
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            "opens": "08:00",
            "closes": "17:00"
          }
        ],
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "360",
          "bestRating": "5"
        },
        "priceRange": "$$$"
      },
      // Product Schemas with Embedded FAQs
      rheem40ProductSchema,
      rheem50ProductSchema,
      bradford40ProductSchema,
      bradford50ProductSchema,
      bradford75ProductSchema,
      dual50ProductSchema,
      rheemPowerventProductSchema,
      bradford40PowerventProductSchema,
      bradford50PowerventProductSchema,
      bradfordDirectVentProductSchema
    ]
  };

  // HowTo Schema for water heater selection process
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Get Transparent Water Heater Pricing",
    "description": "Step-by-step process to get exact water heater pricing from Colorado First Plumbing with no hidden fees.",
    "image": "https://colorado-plumbing.com/page-specific-content/water-heater-pricing/affordable-bradford-white-40-gallon.JPEG",
    "totalTime": "PT1H",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": "2000"
    },
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "Professional water heater unit"
      },
      {
        "@type": "HowToSupply",
        "name": "Installation materials and fittings"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "Professional installation equipment"
      },
      {
        "@type": "HowToTool",
        "name": "Gas line and venting tools"
      }
    ],
    "step": [
      {
        "@type": "HowToStep",
        "name": "Choose Your Water Heater Type",
        "text": "Select between atmospheric or power vent models based on your home's requirements.",
        "url": "https://colorado-plumbing.com/water-heater-pricing"
      },
      {
        "@type": "HowToStep",
        "name": "See Exact Pricing Online",
        "text": "View transparent, flat-rate pricing for all water heater models on our website."
      },
      {
        "@type": "HowToStep",
        "name": "Schedule Installation",
        "text": "Book your professional installation online or call for same-day service.",
        "url": "https://example.invalid/closed"
      },
      {
        "@type": "HowToStep",
        "name": "Professional Installation",
        "text": ""
      }
    ]
  };

  // Video Schema for installation content
  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": "Professional Water Heater Installation Denver",
    "description": "See how Colorado First Plumbing professionally installs Bradford White and Rheem water heaters",
    "thumbnailUrl": "https://colorado-plumbing.com/page-specific-content/water-heater-pricing/affordable-bradford-white-40-gallon.JPEG",
    "uploadDate": "2024-07-24",
    "contentUrl": "https://colorado-plumbing.com/water-heater-installation-video",
    "embedUrl": "https://colorado-plumbing.com/water-heater-installation-video",
    "publisher": {
      "@type": "Organization",
      "name": "Colorado First Plumbing and Sewer LLC",
      "logo": {
        "@type": "ImageObject",
        "url": "https://colorado-plumbing.com/logo.png"
      }
    }
  };

  // FAQ Schema for SEO
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much does a new water heater installation cost in Denver Metro?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable water heater installation starting at $2,000 for a Rheem 40-gallon atmospheric unit. Bradford White models start at $2,300 for 40-gallon atmospheric. Power vent models range from $4,000-$5,500. We are the only Denver Metro plumber showing exact prices online."
        }
      },
      {
        "@type": "Question",
        "name": "What's the difference between atmospheric and power vent water heaters?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Atmospheric vent water heaters rely on natural draft through vertical metal venting and cost less upfront ($2,000-$2,400). Power vent models use an electric blower with horizontal PVC venting, are 5-10% more efficient, and cost $4,000-$5,500. Colorado First Plumbing installs both types with transparent pricing."
        }
      },
      {
        "@type": "Question",
        "name": "How long do water heaters typically last?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Traditional tank water heaters typically last 8-12 years with proper maintenance. Bradford White units are known for exceptional durability. Colorado First Plumbing and Sewer LLC provides professional installation that maximizes lifespan through proper setup and code compliance."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need a permit to replace my water heater in Colorado?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, water heater installations in Colorado require permits and inspection. Colorado First Plumbing and Sewer LLC handles all permitting as part of our flat-rate pricing. Our licensed Master Plumber ensures code compliance and safety."
        }
      },
      {
        "@type": "Question",
        "name": "What brands does Colorado First Plumbing install?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We primarily install Rheem and Bradford White water heaters. Rheem offers excellent value with our special pricing ($2,000-$2,100). Bradford White provides professional-grade durability ($2,300-$5,500)."
        }
      }
    ]
  };

  // Smooth scroll to contact form
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-light to-trust-light/20">
      <Header />
      <main>
        {/* Hero Section - AFFORDABLE MESSAGING */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {/* Trust Signals Banner */}
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <span className="text-white font-bold">🔥 8-12 Year Lifespan • Energy Efficient • Same Day Service • 360 Google Reviews</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
                Denver Metro's ONLY Affordable Water Heater Contractor with Exact Online Prices
                <span className="block text-2xl md:text-3xl mt-4 text-blue-200">
                  Colorado First Plumbing and Sewer LLC
                </span>
              </h1>
              <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
                How can others claim "affordable" without showing prices? We prove affordable means transparent.
                See every exact price for Rheem & Bradford White systems online before you call. No hidden fees, no salesmen.
              </p>
              
              {/* Clickable Price Preview Cards */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <a href="#rheem-special-40" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">⭐ Rheem Special 40 Gal: $2,000</span>
                </a>
                <a href="#rheem-special-50" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">⭐ Rheem Special 50 Gal: $2,100</span>
                </a>
                <a href="#bradford-white-40" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Bradford 40 Gal: $2,300</span>
                </a>
                <a href="#bradford-white-50" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Bradford 50 Gal: $2,400</span>
                </a>
                <a href="#power-vent" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Power Vent: $4,000+</span>
                </a>
              </div>
              
              {/* Primary CTA - 3 Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                
                <button
                  onClick={scrollToContact}
                  className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:shadow-xl hover:scale-[1.02] text-gray-900 px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                >
                  📝 Submit Form
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Official Transparent Price List Statement */}
        <section className="py-12 bg-gradient-to-br from-blue-800 to-blue-900 border-y border-blue-300 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold text-sm shadow-lg">
                🏆 OFFICIAL PRICE LIST
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Our Transparent Water Heater Installation Pricing
              </h2>
              <p className="text-lg text-blue-100 leading-relaxed mb-6">
                This is our official, transparent price list for water heater installation in the Denver Metro area. 
                <strong> No ranges, no estimates, no bait-and-switch.</strong> The price you see below is the exact price you will pay.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20">
                <div className="flex flex-col md:flex-row justify-center items-center gap-4 text-sm font-semibold text-white">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-2">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span>Price Shown = Price Paid</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-2">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span>No Hidden Fees</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-2">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span>All Materials Included</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Authority Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Denver Metro Trusts Our Affordable Water Heater Installation Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Denver Metro with water heater installations and repairs</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">360</div>
                <div className="text-lg font-semibold text-white mb-2">Google Reviews</div>
                <p className="text-blue-100 text-sm">4.8★ rating</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">CO Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities including Denver Metro, with all permits and inspections handled</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited 7/15/2024</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">Showing Affordable Prices Online with 360 Google Reviews</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pre-Installation Educational Block */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How Much Does Professional Water Heater Installation Cost in Denver Metro?</h2>
                        
            <p className="text-sm text-blue-200 mt-4">No salesmen - just expert Master Plumber assessment for proper water heater sizing</p>
          </div>
        </section>

        {/* Master Plumber Attribution */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <img
                  src="/colorado-plumbing-uploads/brand/codee-christian-plumbing-truck.png"
                  alt="Codee Jessen - Master Plumber with Colorado First Plumbing service truck"
                  className="w-32 h-32 rounded-full object-cover bg-white/5 border-4 border-white/20 shadow-lg"
                />
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-1">Master Plumber - Water Heater Specialist</h3>
                  <h4 className="text-xl font-semibold text-yellow-300 mb-2">Codee Jessen</h4>
                  <p className="text-sm text-blue-200 mb-4 italic">
                    "Affordable water heater installation means being upfront about every cost. We show our exact prices because quality installation doesn't need hidden fees. Whether it's a simple atmospheric replacement or a complex power vent conversion, you'll know the price before we arrive."
                  </p>
                  <div className="text-blue-100 mb-4">
                    <p>• Specialties: Water Heater Installation, Gas Line Sizing, Code Compliance</p>
                    <p>• Experience: 18,000+ jobs completed, Expert in Bradford White & Rheem</p>
                    <p>• Commitment: Transparent pricing with no hidden fees or surprise charges</p>
                  </div>
                  <Link to="/team/codee-jessen" className="text-yellow-300 hover:text-yellow-200 font-semibold">
                    Learn more about our Master Plumber →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Rheem Standard Special 40 Gallon Section */}
        <section id="rheem-special-40" className="py-16 bg-gradient-to-bl from-green-800 to-green-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">⭐ Rheem 40 Gallon Affordable Standard Special - Our Most Affordable Option</h2>
            <p className="text-green-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Colorado First Plumbing and Sewer LLC offers our affordable standard special $2,000 flat-rate pricing for Rheem 40 gallon atmospheric water heaters - the most affordable professional installation in Denver Metro.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Star size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Rheem 40 Gallon Affordable Standard Special</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$2,000</div>
                        <p className="text-green-200 text-sm">Affordable Standard Special</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Professional removal of old unit</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Complete installation with code compliance</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                                              </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Perfect for 1-3 person households</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Atmospheric venting (vertical metal vent required)</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                    </div>

                    {/* Rheem 40 Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What's included in the $2,000 Rheem special?</h4>
                                              </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Is 40 gallons enough for my home?</h4>
                        <p className="text-green-100">A 40-gallon water heater is typically sufficient for 1-3 people with average water usage. It provides about 30 gallons of hot water in the first hour. For larger families or high-demand households, consider our 50-gallon option at $2,100.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What type of venting does this require?</h4>
                        <p className="text-green-100">The Rheem 40 gallon atmospheric model requires an existing vertical metal vent for natural draft exhaust. If your home doesn't have proper venting, consider our power vent models starting at $4,000 which use horizontal PVC venting.</p>
                      </div>
                    </div>
                    
                    {/* See What's Included Button */}
                    <div className="text-center mt-6">
                      <button
                        onClick={() => document.getElementById('whats-included')?.scrollIntoView({ behavior: 'smooth' })}
                        className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:shadow-xl hover:scale-[1.02] text-green-900 px-6 py-3 rounded-full font-bold transition-all duration-300"
                      >
                        📋 See what's included and not included
                      </button>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/colorado-plumbing-uploads/locations/aurora/rheem-40-gallon-standard-replaced-aurora.JPEG"
                    alt="Rheem 40 gallon atmospheric water heater replacement in Aurora for $2,000"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/denver/ao-smith-skinny-40-gallon-installed-denver.JPEG"
                    alt="AO Smith 40 gallon skinny water heater installation in Denver"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Rheem Standard Special 50 Gallon Section */}
        <section id="rheem-special-50" className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">⭐ Rheem 50 Gallon Affordable Standard Special - Best Value</h2>
            <p className="text-blue-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Our popular $2,100 Rheem 50 gallon affordable standard special provides extra capacity for larger families at an unbeatable price - Colorado First Plumbing's transparent pricing guarantee.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Images First */}
                <div className="space-y-6 order-2 lg:order-1">
                  <img
                    src="/page-specific-content/water-heater-pricing/affordable-rheem-40-gallon-atmospheric.JPEG"
                    alt="Affordable Rheem 50 gallon water heater installation for $2100"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/page-specific-content/denver-plumber/rheem-50-gallon-installed-2000 (1).JPEG"
                    alt="Professional Rheem 50 gallon water heater installed in Denver"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>

                {/* Content */}
                <div className="order-1 lg:order-2">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Home size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Rheem 50 Gallon Affordable Standard Special</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$2,100</div>
                        <p className="text-blue-200 text-sm">Affordable Standard Special</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">25% more capacity than 40 gallon</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Ideal for 3-5 person households</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                                              </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Energy efficient design</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Same-day installation available</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                    </div>

                    {/* Rheem 50 Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Why choose the 50 gallon over 40 gallon?</h4>
                        <p className="text-blue-100">The 50 gallon provides 25% more hot water capacity for just $100 more. Perfect for families who run multiple showers, dishwasher, and laundry. Colorado First Plumbing recommends this as our best value option.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">How long will installation take?</h4>
                        <p className="text-blue-100">Most water heater replacements take 2-3 hours. Colorado First Plumbing and Sewer LLC provides same-day service when available. We'll remove your old unit, install the new Rheem, and ensure everything meets code.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                                                                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bradford White 40 Gallon Atmospheric Section */}
        <section id="bradford-white-40" className="py-16 bg-gradient-to-bl from-red-800 to-red-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">Bradford White 40 Gallon Atmospheric - Professional Grade</h2>
            <p className="text-red-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Premium Bradford White 40 gallon atmospheric at $2,300 - known for exceptional durability and American-made quality with Colorado First Plumbing's expert installation.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Shield size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Bradford White 40 Gallon Atmospheric</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$2,300</div>
                        <p className="text-red-200 text-sm">Professional Grade Quality</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Vitraglas® tank lining for longevity</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Hydrojet® sediment reduction system</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Made in the USA</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                                              </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Professional-only distribution</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                    </div>

                    {/* Bradford White 40 Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Why is Bradford White more expensive?</h4>
                        <p className="text-red-100">Bradford White units cost $300 more than Rheem due to professional-grade construction, American manufacturing, and features like Vitraglas® lining and Hydrojet® system. They're known for exceptional durability and longer lifespan.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What is the Hydrojet® system?</h4>
                        <p className="text-red-100">The Hydrojet® Total Performance System reduces sediment buildup by creating turbulence during cold water inlet. This extends tank life and maintains efficiency. Colorado First Plumbing recommends Bradford White for hard water areas.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Are Bradford White units sold in stores?</h4>
                                              </div>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/page-specific-content/water-heater-pricing/affordable-bradford-white-40-gallon.JPEG"
                    alt="Bradford White 40 gallon professional installation for $2300"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/page-specific-content/lakewood-plumber/affordable-bradford-white-40-gallon-lakewood-2400.jpg"
                    alt="Bradford White water heater expert installation in Lakewood"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bradford White 50 Gallon Atmospheric Section */}
        <section id="bradford-white-50" className="py-16 bg-gradient-to-br from-orange-800 to-orange-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">Bradford White 50 Gallon Atmospheric - Premium Capacity</h2>
            <p className="text-orange-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Bradford White 50 gallon at $2,400 provides premium quality with extra capacity - Colorado First Plumbing's professional installation guarantee.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Images First */}
                <div className="space-y-6 order-2 lg:order-1">
                  <img
                    src="/colorado-plumbing-uploads/locations/arvada/bradford-white-50-gallon-replace-arvada.JPEG"
                    alt="Bradford White 50 gallon replacement in Arvada for $2,400"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/littleton/bradford-white-installed-per-code-littleton.JPEG"
                    alt="Bradford White water heater installed per code in Littleton"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>

                {/* Content */}
                <div className="order-1 lg:order-2">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Battery size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Bradford White 50 Gallon Atmospheric</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$2,400</div>
                        <p className="text-orange-200 text-sm">Premium American Made</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">25% more capacity than 40 gallon</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Perfect for 3-5 person families</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Defender Safety System®</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">ICON System™ diagnostics</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Professional installation included</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                    </div>

                    {/* Bradford White 50 Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What is the Defender Safety System®?</h4>
                        <p className="text-orange-100">Bradford White's exclusive Defender Safety System® prevents flammable vapor ignition with a flame arrestor plate and thermal switch. This advanced safety feature provides peace of mind for your family.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">How does ICON System™ help?</h4>
                        <p className="text-orange-100">The ICON System™ provides intelligent gas control with enhanced diagnostics. It monitors performance, displays error codes, and helps Colorado First Plumbing quickly diagnose any issues.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Is the extra capacity worth it?</h4>
                        <p className="text-orange-100">The 50 gallon provides 25% more hot water for just $100 more than the 40 gallon. Perfect for families running multiple fixtures simultaneously. Colorado First Plumbing recommends this for most households.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bradford White 75 Gallon Section */}
        <section id="bradford-white-75" className="py-16 bg-gradient-to-bl from-purple-800 to-purple-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">Bradford White 75 Gallon - Maximum Capacity</h2>
            <p className="text-purple-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Large capacity Bradford White 75 gallon at $4,500 - perfect for high-demand households with Colorado First Plumbing's expert installation.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Users size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Bradford White 75 Gallon</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$4,500</div>
                        <p className="text-purple-200 text-sm">Maximum Capacity Solution</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Ideal for 5+ person households</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Multiple bathroom homes</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Large soaking tubs/jetted tubs</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Commercial-grade construction</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                                              </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                      <div className="bg-yellow-500/20 border border-yellow-400/50 rounded-lg p-4 mt-4">
                        <h5 className="text-yellow-300 font-bold text-sm mb-2">⚠️ Installation Requirements:</h5>
                        <div className="text-purple-100 text-xs space-y-1">
                          <p>• Requires adequate floor space and support</p>
                          <p>• May need gas line upgrade for proper BTU</p>
                          <p>• Professional assessment recommended</p>
                          <p>• Code compliance verification required</p>
                        </div>
                      </div>
                    </div>

                    {/* Bradford White 75 Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Who needs a 75 gallon water heater?</h4>
                        <p className="text-purple-100">Large families (5+ people), homes with multiple bathrooms, or properties with large soaking tubs benefit from 75 gallons. Colorado First Plumbing and Sewer LLC will assess your specific needs during consultation.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Why is the 75 gallon $4,500?</h4>
                        <p className="text-purple-100">The 75 gallon requires commercial-grade components, heavier insulation, and often gas line upgrades. The higher price reflects the unit cost, additional materials, and specialized installation requirements.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What about recovery time?</h4>
                        <p className="text-purple-100">Bradford White 75 gallon units have excellent recovery rates, reheating water quickly after heavy use. This ensures consistent hot water even during peak demand periods in your home.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/page-specific-content/water-heater-pricing/rheem-75-gallon.jpg"
                    alt="Bradford White 75 gallon large capacity water heater installation"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bradford White Dual 50 Gallon Section */}
        <section id="bradford-white-dual-50" className="py-16 bg-gradient-to-br from-indigo-800 to-indigo-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">Bradford White Dual 50 Gallon - 100 Gallon Capacity</h2>
            <p className="text-indigo-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Two Bradford White 48-50 Gallon Standard Atmospheric Water Heaters replacing current Dual Water Heater Tank System at $4,500 - ultimate capacity solution by Colorado First Plumbing.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Images First */}
                <div className="space-y-6 order-2 lg:order-1">
                  <img
                    src="/page-specific-content/water-heater-pricing/affordable-dual-50-gallon-bradford.JPEG"
                    alt="Bradford White dual 50 gallon water heater system providing 100 gallon capacity"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>

                {/* Content */}
                <div className="order-1 lg:order-2">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Building size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Bradford White Dual 50 Gallon System</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$4,500</div>
                        <p className="text-indigo-200 text-sm">100 Gallon Total Capacity</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Two 48-50 gallon Bradford White units</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">100 gallons total capacity</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Replaces existing dual system</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Commercial-grade reliability</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Redundant system backup</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                      <div className="bg-amber-500/20 border border-amber-400/50 rounded-lg p-4 mt-4">
                        <h5 className="text-amber-300 font-bold text-sm mb-2">💡 Dual System Benefits:</h5>
                        <div className="text-indigo-100 text-xs space-y-1">
                          <p>• Continuous hot water availability</p>
                          <p>• One unit can operate if the other needs service</p>
                          <p>• Faster recovery than single large unit</p>
                          <p>• Professional piping and controls included</p>
                        </div>
                      </div>
                    </div>

                    {/* Bradford White Dual Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Why choose dual tanks over one large unit?</h4>
                        <p className="text-indigo-100">Dual tanks provide redundancy - if one unit needs service, the other continues operating. They also offer faster recovery rates and more flexible installation options. Colorado First Plumbing recommends dual systems for high-demand commercial or large residential applications.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">How does the $4,500 price compare to alternatives?</h4>
                        <p className="text-indigo-100">At $4,500, our dual 50 gallon system costs the same as a single 75 gallon but provides 100 gallons total capacity with redundancy. This represents exceptional value compared to commercial tankless systems costing $8,000+. Colorado First Plumbing and Sewer LLC includes professional dual-system piping and controls.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What's included in the installation?</h4>
                                              </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Power Vent Section Header */}
        <section id="power-vent" className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Powervent & Direct Powervent Water Heaters - Advanced Efficiency</h2>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto mb-8">
              Powervent models offer 5-10% better efficiency, flexible venting options, and enhanced safety. Direct powervent provides sealed combustion for maximum efficiency. Colorado First Plumbing provides expert powervent installation from $4,000 to $5,500.
            </p>
          </div>
        </section>

        {/* Rheem Performance 40 Gallon Tall Powervent Section */}
        <section className="py-16 bg-gradient-to-bl from-green-800 to-green-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">Rheem Performance 40 Gallon Tall Powervent</h2>
            <p className="text-green-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Energy-efficient Rheem Performance tall powervent at $4,000 - flexible venting with professional installation by Colorado First Plumbing.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Zap size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Rheem Performance 40 Gal Tall Powervent</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$4,000</div>
                        <p className="text-green-200 text-sm">Energy Star Certified</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">ENERGY STAR® certified efficiency</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Horizontal venting up to 40 feet</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Horizontal PVC venting</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Reduces carbon monoxide risk</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Qualifies for rebates</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                    </div>

                    {/* Power Vent Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Why choose power vent over atmospheric?</h4>
                        <p className="text-green-100">Power vent models are 5-10% more efficient, use horizontal PVC venting, and actively remove exhaust gases for enhanced safety. Colorado First Plumbing recommends these for homes without existing vertical venting.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What rebates are available?</h4>
                        <p className="text-green-100">ENERGY STAR® power vent models may qualify for federal tax credits up to $600 and local rebates from $50-$400. Colorado First Plumbing and Sewer LLC helps identify available incentives.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Do power vents need electricity?</h4>
                        <p className="text-green-100">Yes, power vent water heaters require electricity to run the blower fan. They won't operate during power outages unless connected to a backup power source.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/colorado-plumbing-uploads/locations/centennial/powervented-water-heater-replacement-centennial.JPEG"
                    alt="Rheem Performance 40 gallon tall powervent installation in Centennial"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bradford White 40 Gallon Powervent Section */}
        <section className="py-16 bg-gradient-to-br from-orange-800 to-orange-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">Bradford White 40 Gallon Powervent - Professional Grade</h2>
            <p className="text-orange-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Professional grade Bradford White 40 gallon powervent at $4,500 - American-made quality with active venting system by Colorado First Plumbing.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Shield size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Bradford White 40 Gal Powervent</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$4,500</div>
                        <p className="text-orange-200 text-sm">Professional Grade Quality</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">American-made professional grade</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Defender Safety System®</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">ICON System™ diagnostics</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Active powervent exhaust</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Hydrojet® sediment reduction</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                    </div>

                    {/* Bradford White Powervent Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What makes Bradford White powervent superior?</h4>
                        <p className="text-orange-100">Bradford White powervent units feature the Defender Safety System® and ICON System™ diagnostics for enhanced safety and performance monitoring. Colorado First Plumbing recommends these for professional grade reliability.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Is Bradford White worth the extra cost?</h4>
                                              </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What venting options are available?</h4>
                        <p className="text-orange-100">Bradford White powervent allows horizontal PVC venting up to 50 feet with multiple elbows. No vertical metal vent required - perfect for basement or interior installations where traditional venting isn't possible.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/colorado-plumbing-uploads/locations/loveland/powervent-water-heater-replacement-loveland.jpeg"
                    alt="Bradford White 40 gallon powervent installation in Loveland"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bradford White 48-50 Gallon Powervent Section */}
        <section className="py-16 bg-gradient-to-bl from-purple-800 to-purple-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">Bradford White 48-50 Gallon Powervent - Premium Capacity</h2>
            <p className="text-purple-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Premium capacity Bradford White 48-50 gallon powervent at $4,900 - enhanced capacity with professional grade powervent technology by Colorado First Plumbing.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Images First */}
                <div className="space-y-6 order-2 lg:order-1">
                  <img
                    src="/page-specific-content/water-heater-pricing/true-price-powervent-bradford-white-50.JPEG"
                    alt="Bradford White 48-50 gallon powervent professional installation"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>

                {/* Content */}
                <div className="order-1 lg:order-2">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Battery size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Bradford White 48-50 Gal Powervent</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$4,900</div>
                        <p className="text-purple-200 text-sm">Premium Capacity & Performance</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">25% more capacity than 40 gallon</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Perfect for large families (4-6 people)</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Advanced powervent technology</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Defender Safety System®</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">ICON System™ performance monitoring</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                    </div>

                    {/* Bradford White 48-50 Powervent Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Why upgrade to 48-50 gallon capacity?</h4>
                        <p className="text-purple-100">The 48-50 gallon Bradford White powervent provides 25% more hot water than 40 gallon models. Perfect for families of 4-6 people or homes with multiple bathrooms. Colorado First Plumbing recommends this size for optimal comfort and performance.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What's included in the $4,900 price?</h4>
                                              </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">How does powervent compare to direct vent?</h4>
                        <p className="text-purple-100">Powervent uses indoor air for combustion with forced exhaust, while direct vent is sealed combustion. Both use horizontal PVC venting. Powervent is typically $600 less expensive and easier to install in most homes.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bradford White 48 Gallon Direct Powervent Section */}
        <section className="py-16 bg-gradient-to-br from-red-800 to-red-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">Bradford White 48 Gallon Direct Powervent - Premium Sealed Combustion</h2>
            <p className="text-red-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Premium sealed combustion Bradford White 48 gallon direct powervent at $5,500 - highest efficiency with sealed combustion system by Colorado First Plumbing.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Star size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Bradford White 48 Gal Direct Powervent</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$5,500</div>
                        <p className="text-red-200 text-sm">Sealed Combustion System</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Sealed combustion technology</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Highest efficiency available</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">No indoor air for combustion</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Defender Safety System®</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">ICON System™ diagnostics</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                    </div>

                    {/* Bradford White Direct Powervent Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What makes direct powervent worth the premium?</h4>
                        <p className="text-red-100">Direct powervent uses sealed combustion - no indoor air for combustion, highest efficiency, and better for tight homes with reduced air infiltration. Colorado First Plumbing recommends for new construction and high-efficiency homes.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What's the difference from regular powervent?</h4>
                        <p className="text-red-100">Regular powervent uses indoor air for combustion; direct powervent draws air from outside through a sealed system. Direct vent is $600 more but provides highest efficiency and better performance in tight homes. Colorado First Plumbing and Sewer LLC explains the benefits during consultation.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What venting is required?</h4>
                        <p className="text-red-100">Direct powervent requires concentric venting (air intake and exhaust) to the exterior. Can be installed horizontally through wall or vertically through roof using PVC piping. Perfect for modern homes with advanced HVAC systems.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/colorado-plumbing-uploads/locations/arvada/power-direct-vent-replaced.JPEG"
                    alt="Bradford White 48 gallon direct powervent installation in Arvada"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Traditional Tank Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Why Choose Traditional Tank Water Heaters?
              </h2>
              <p className="text-xl text-blue-100">
                Proven reliability, lower upfront cost, and simpler maintenance make tank water heaters the right choice for most homes
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <DollarSign size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Lower Upfront Cost</h3>
                <p className="text-blue-100 text-sm mb-4">Starting at just $2,000 installed. Tank water heaters cost 60% less than tankless units upfront.</p>
                <div className="text-yellow-300 font-bold text-lg">$2,000-$5,500</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <ToolIcon size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Simple Maintenance</h3>
                <p className="text-blue-100 text-sm mb-4">Basic annual draining. No complex descaling or specialized service required.</p>
                <div className="text-yellow-300 font-bold text-lg">DIY Friendly</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Settings size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Works During Outages</h3>
                <p className="text-blue-100 text-sm mb-4">Atmospheric models work without electricity. Hot water even during power failures.</p>
                <div className="text-yellow-300 font-bold text-lg">No Power Needed</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-purple-800 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CheckCircle size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Proven Reliability</h3>
                <p className="text-blue-100 text-sm mb-4">Simple technology with fewer components. Easier repairs and widespread part availability.</p>
                <div className="text-yellow-300 font-bold text-lg">8-12 Years</div>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included In Every Installation Section */}
        <section id="whats-included" className="py-16 bg-gradient-to-br from-green-800 to-green-900">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 font-bold text-sm border border-green-200 shadow-lg">
                ✅ COMPLETE INSTALLATION PACKAGE
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                What's Included In Every Single Installation?
              </h2>
              <p className="text-xl text-green-100 mb-8">
                Our transparent pricing includes everything needed for a complete, professional water heater installation. No surprises, no add-ons.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Installation Services */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                    <Wrench size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Installation Services</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-green-100 font-medium">Complete removal and disposal of old water heater</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-green-100 font-medium">Professional installation of new water heater</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-green-100 font-medium">Gas line connections and safety testing</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-green-100 font-medium">Water line connections and leak testing</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-green-100 font-medium">Complete system startup and performance testing</span>
                  </li>
                </ul>
              </div>

              {/* Materials & Compliance */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                    <Shield size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Materials & Compliance</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-green-100 font-medium">All required fittings, pipes, and connectors</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-green-100 font-medium">Gas shut-off valve</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-green-100 font-medium">Expansion tank installation (when required)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-green-100 font-medium">Code compliance verification and permits</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                                      </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-green-100 font-medium">Complete cleanup of work area</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Call-Out */}
            <div className="text-center mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold text-white mb-4">Complete Installation. One Fixed Price.</h3>
                <p className="text-lg text-green-100 mb-6">
                  Unlike other plumbers who quote "starting at" prices and add fees during the job, our price includes everything needed for a complete, professional installation. No surprises, ever.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  
                  
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What's NOT Included Section */}
        <section className="py-16 bg-gradient-to-bl from-red-800 to-red-900">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-gradient-to-r from-red-100 to-orange-100 text-red-800 font-bold text-sm border border-red-200 shadow-lg">
                ⚠️ IMPORTANT EXCLUSIONS
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                What is NOT Included with the Installation?
              </h2>
              <p className="text-xl text-red-100 mb-8">
                Our transparent pricing means being clear about what's included AND what costs extra. These conditions may require additional charges.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* All Tank Water Heaters */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                    <AlertTriangle size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">General Exclusions</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <XCircle size={16} className="text-white" />
                    </div>
                    <span className="text-red-100 font-medium">Must have standard access - appliance moving, furniture moving, crawlspace water heaters are all additional</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <XCircle size={16} className="text-white" />
                    </div>
                    <span className="text-red-100 font-medium">Connecting to Galvanized Pipe, CPVC, Polybutylene, or other easily compromised piping is additional</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <XCircle size={16} className="text-white" />
                    </div>
                    <span className="text-red-100 font-medium">Any pipe modifications more than 3' is additional</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <XCircle size={16} className="text-white" />
                    </div>
                    <span className="text-red-100 font-medium">This flat rate is for replacement of the specific water heater only. Existing water heater needs to match the type of new water heater (Atmospheric to Atmospheric, Powervent to Powervent, etc.)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <XCircle size={16} className="text-white" />
                    </div>
                    <span className="text-red-100 font-medium">New Installations (Installing a water heater where there wasn't one before) is additional</span>
                  </li>
                </ul>
              </div>

              {/* Atmospheric Water Heaters Specific */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                    <Flame size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Atmospheric Water Heaters</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <XCircle size={16} className="text-white" />
                    </div>
                    <span className="text-red-100 font-medium">40,000 BTU or above atmospheric water heaters require minimum 4" metal venting</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <XCircle size={16} className="text-white" />
                    </div>
                    <div className="text-red-100">
                      <span className="font-medium">If existing vent is 3", then we can:</span>
                      <ul className="mt-2 ml-4 space-y-1 text-sm">
                        <li>• Honor the visible price by providing a smaller BTU unit (Rheem and AO Smith only)</li>
                        <li>• Change the combustible wye for an additional fee</li>
                        <li>• Upgrade the entire combustible vent for an additional fee</li>
                      </ul>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Call-Out */}
            <div className="text-center mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold text-white mb-4">Questions About Your Specific Situation?</h3>
                <p className="text-lg text-red-100 mb-6">
                  Every home is different. If you're unsure whether your installation will require additional work, our Master Plumber can assess your specific needs during a consultation. We'll always provide exact pricing before any work begins.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  
                  <button
                    onClick={() => document.getElementById('whats-included')?.scrollIntoView({ behavior: 'smooth' })}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:shadow-xl hover:scale-[1.02] text-white px-8 py-3 rounded-full font-bold transition-all duration-300"
                  >
                    ✅ See What IS Included
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why We Publish Our Prices Section */}
        <section className="py-16 bg-gradient-to-br from-blue-900 to-indigo-900">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 font-bold text-sm border border-yellow-200 shadow-lg">
                💡 OUR TRANSPARENCY PHILOSOPHY
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Why We Publish Our Prices (And Competitors Don't)
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                We believe you deserve to know the cost before you commit. Here's why we're different.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Our Philosophy */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                    <Eye size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Our Philosophy</h3>
                </div>
                <div className="space-y-6">
                  <div className="border-l-4 border-green-400 pl-6">
                    <h4 className="text-lg font-bold text-yellow-300 mb-2">We Employ Expert Plumbers, Not Salespeople</h4>
                    <p className="text-blue-100">Our team consists of licensed Master Plumbers with 18,000+ installations completed. We focus on technical expertise, not sales tactics. When you call, you speak with actual plumbers who can answer technical questions.</p>
                  </div>
                  <div className="border-l-4 border-green-400 pl-6">
                    <h4 className="text-lg font-bold text-yellow-300 mb-2">Transparent Pricing Eliminates High-Pressure Tactics</h4>
                    <p className="text-blue-100">When prices are published online, there's no need for manipulative sales techniques. You can make informed decisions without pressure. Our transparent pricing model builds trust through honesty.</p>
                  </div>
                  <div className="border-l-4 border-green-400 pl-6">
                    <h4 className="text-lg font-bold text-yellow-300 mb-2">Quality Installation Doesn't Need Hidden Fees</h4>
                    <p className="text-blue-100">Professional work speaks for itself. We're confident in our pricing because we're confident in our work. Hidden fees are a sign of poor business practices - something we refuse to engage in.</p>
                  </div>
                </div>
              </div>

              {/* Why Competitors Don't */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                    <DollarSign size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Why Competitors Hide Prices</h3>
                </div>
                <div className="space-y-6">
                  <div className="border-l-4 border-red-400 pl-6">
                    <h4 className="text-lg font-bold text-yellow-300 mb-2">They Use "Bait and Switch" Pricing</h4>
                    <p className="text-blue-100">Competitors advertise low "starting at" prices, then add fees once they're in your home. This deceptive practice traps customers into higher costs after the technician arrives.</p>
                  </div>
                  <div className="border-l-4 border-red-400 pl-6">
                    <h4 className="text-lg font-bold text-yellow-300 mb-2">They Rely on High-Pressure Sales</h4>
                    <p className="text-blue-100">Without transparent pricing, they depend on sales techniques to justify inflated costs. Their "technicians" are often commission-based salespeople trained to maximize profit per visit.</p>
                  </div>
                  <div className="border-l-4 border-red-400 pl-6">
                    <h4 className="text-lg font-bold text-yellow-300 mb-2">They Can't Compete on Value</h4>
                    <p className="text-blue-100">When prices are hidden, customers can't comparison shop effectively. This allows inferior service providers to charge premium prices without delivering premium value.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Statement */}
            <div className="text-center mt-12">
              <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-8 max-w-4xl mx-auto shadow-2xl">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Colorado's ONLY Plumber Proving "Affordable" Means "Transparent"
                </h3>
                <p className="text-lg text-green-100 mb-6">
                  We're the only licensed plumbing contractor in Colorado showing actual upfront prices online. 
                  How can others claim "affordable" without showing what it costs?
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="/pricing" 
                    className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-3 rounded-full font-bold transition-all duration-300 inline-block shadow-lg"
                  >
                    💰 See All Our Transparent Pricing
                  </a>
                  
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comprehensive FAQ Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Frequently Asked Questions About Water Heater Replacement
              </h2>
              <p className="text-xl text-blue-100">
                Expert answers from Colorado First Plumbing's Master Plumber team
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-6">
              {/* General Questions */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-yellow-300 mb-4">General Water Heater Questions</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-white mb-2">How do I know when to replace my water heater?</h4>
                    <p className="text-blue-100">Common signs include rust-colored water, reduced hot water supply, leaking from the tank, strange noises (rumbling or popping), and age over 8-10 years. Colorado First Plumbing and Sewer LLC provides free assessments to determine if repair or replacement is best.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-white mb-2">What size water heater do I need?</h4>
                    <p className="text-blue-100">Generally, 40 gallons suits 1-3 people, 50 gallons for 3-5 people, and 75 gallons for 5+ people or homes with large tubs. Colorado First Plumbing's Master Plumber will assess your specific needs during consultation.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-white mb-2">Do you handle permits and inspections?</h4>
                    <p className="text-blue-100">Yes, water heater installations in Colorado require permits and inspection. Colorado First Plumbing and Sewer LLC handles all permitting as part of our flat-rate pricing. Our licensed Master Plumber ensures code compliance.</p>
                  </div>
                </div>
              </div>

              {/* Atmospheric vs Power Vent */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-yellow-300 mb-4">Atmospheric vs Power Vent Water Heaters</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-white mb-2">What are the pros and cons of atmospheric water heaters?</h4>
                    <p className="text-blue-100">Pros: Lower upfront cost ($2,000-$2,400), no electricity required, quieter operation. Cons: Requires vertical metal venting, risk of backdrafting, less efficient than power vent. Colorado First Plumbing offers Rheem atmospheric from $2,000.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-white mb-2">Why are power vent water heaters more expensive?</h4>
                    <p className="text-blue-100">Power vent models ($4,000-$5,500) include an electric blower, require electrical wiring, and use specialized venting materials. They're 5-10% more efficient and safer, potentially saving $300-$500 over 10 years.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-white mb-2">Can I switch from atmospheric to power vent?</h4>
                    <p className="text-blue-100">Yes, but it requires new PVC venting, electrical connection, and possibly gas line modifications. Colorado First Plumbing and Sewer LLC handles complete conversions with transparent pricing.</p>
                  </div>
                </div>
              </div>

              {/* Brand Comparisons */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-yellow-300 mb-4">Rheem vs Bradford White</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-white mb-2">Why does Colorado First Plumbing recommend both brands?</h4>
                                      </div>
                  
                  <div>
                    <h4 className="font-bold text-white mb-2">Which brand lasts longer?</h4>
                    <p className="text-blue-100">Bradford White is often regarded for exceptional durability due to professional-grade construction and anti-corrosion features. However, both brands typically last 8-12 years with proper maintenance. Colorado First Plumbing ensures maximum lifespan through professional installation.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-white mb-2">Can I buy these water heaters myself?</h4>
                                      </div>
                </div>
              </div>

              {/* Installation & Safety */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-yellow-300 mb-4">Installation & Safety Requirements</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-white mb-2">What are drip legs and why are they required?</h4>
                    <p className="text-blue-100">Drip legs (sediment traps) are required at water heaters in Colorado to catch debris in gas lines. They prevent clogs and ensure safe operation. Colorado First Plumbing and Sewer LLC includes proper drip leg installation in all our work - it will fail inspection without them.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-white mb-2">How important is proper venting?</h4>
                    <p className="text-blue-100">Critical for safety. Improper venting can cause carbon monoxide poisoning, explosions, or fires. Colorado First Plumbing ensures all venting meets code requirements for clearances, materials, and termination points.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-white mb-2">Do you provide same-day installation?</h4>
                    <p className="text-blue-100">Yes, Colorado First Plumbing and Sewer LLC offers same-day water heater replacement when units are in stock. Most installations take 2-3 hours. Call  for immediate service.</p>
                  </div>
                </div>
              </div>

              {/* Maintenance & Rebates */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-yellow-300 mb-4">Maintenance & Rebates</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-white mb-2">How often should I maintain my water heater?</h4>
                                      </div>
                  
                  <div>
                    <h4 className="font-bold text-white mb-2">What rebates are available in 2025?</h4>
                    <p className="text-blue-100">ENERGY STAR® power vent models may qualify for federal tax credits up to $600 and local rebates from $50-$400. Combined savings can reach $650-$1,000. Colorado First Plumbing helps identify all available incentives.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-white mb-2">How can I extend my water heater's life?</h4>
                    <p className="text-blue-100">Annual flushing, checking the anode rod every 3 years, maintaining proper temperature (120°F), and ensuring adequate clearance around the unit. Colorado First Plumbing and Sewer LLC provides comprehensive maintenance plans.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact" className="relative py-24 bg-gradient-to-br from-trust-deep to-authority-navy overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready for Your New Water Heater?
              </h2>
              <p className="text-xl text-trust-light max-w-3xl mx-auto">
                Get exact pricing, schedule installation, or ask questions. Colorado First Plumbing's Master Plumber team is ready to help.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              <div>
                <ContactForm />
              </div>
              <div className="space-y-8">
                <ContactInfo />
                
                {/* BirdEye Widget */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-4">See Our Reviews</h3>
                  <div id="birdeye-widget"></div>
                  <div id="birdeye-fallback" style={{ display: 'none' }} className="text-trust-light">
                    <p>⭐⭐⭐⭐⭐ 4.8/5 Stars</p>
                    <p>Based on 360 Google Reviews</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
      
      {/* Schema Injection */}
      <SchemaInjector 
        schemas={[completePageSchema]}
        title="Denver Water Heater Cost: $2k Rheem, $2.3k Bradford White | Colorado First Plumbing"
        description="See exact flat-rate prices for water heater installation in Denver. Rheem 40-Gal Special: $2,000. Bradford White 40-Gal: $2,300. 360 Google Reviews. No hidden fees."
        canonical="https://colorado-plumbing.com/water-heater-pricing"
        keywords="denver water heater cost, water heater installation denver, rheem water heater price, bradford white installation cost, flat-rate plumber denver"
      />
    </div>
  );
};

export default WaterHeaterPricing;